<template>
  <div
    class="is-full-width has-border-light is-radius-large textarea-container has-background-white is-relative is-relative"
    @click="focusQuestionInput"
    :data-e2e="AICypressAttribute.views.CLARA_TEXTAREA_CONTAINER"
  >
    <div
      v-if="$slots.status"
      class="status-message is-absolute xy-t--40 has-background-secondary is-radius-large is-size-6 has-text-weight-semibold px-3 pt-2 pb-6 has-text-grey"
      :data-e2e="AICypressAttribute.views.CLARA_QUESTION_INPUT_STATUS"
    >
      <slot name="status"></slot>
    </div>
    <FormTextarea
      v-model="question"
      ref="questionInput"
      :auto-height="true"
      :show-float-label="false"
      rows="2"
      :read-only="disabled"
      @focus="onQuestionInputFocus"
      @keyup="onKeyUp"
      :placeholder="$t('ai.ask-me-anything')"
      :data-e2e="AICypressAttribute.inputs.CLARA_PROMPT"
    />
    <div class="is-flex is-absolute xy-b-15 xy-r-15">
      <FormMultiSelect
        v-if="authUser.isSuperAdmin && showModelSelector"
        v-model="modelName"
        :config="aiModelSelectConfig"
        :allow-disabled-population="true"
        :disabled="!!presetModelName || disabled"
        class="m-r-10 mb-0"
        :show-float-label="false"
      />
      <ButtonView
        class="button has-text-weight-medium"
        icon-pack="cls"
        icon-left="cls cls-magic-stars"
        :type="isQuestionInputFocused ? 'is-primary' : 'is-light'"
        :disabled="!question || disabled"
        :loading="isAnswering"
        @click="onQuestionSubmit"
        :data-e2e="AICypressAttribute.buttons.ASK_CLARA"
      >
        {{ $t('ai.ask-clara') }}
      </ButtonView>
    </div>
  </div>
</template>

<script>
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import FormMultiSelect from '@/modules/core/form/components/FormMultiSelect.vue';
import FormTextarea from '@/modules/core/form/components/FormTextarea.vue';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { AiModelSelectConfig } from '@/modules/ai/models/AiModelSelectConfig';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import { OpenAIModel } from '@/modules/ai/constants/OpenAIModel';
import { KeyCode } from '@/modules/core/ui/ui.constants';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ClaraQuestionInput',
  emits: ['input', 'on-submit', 'on-question-input-focus', 'on-keyup'],
  mixins: [HasAuthUserMixin],
  components: {
    ButtonView,
    FormTextarea,
    FormMultiSelect
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    isQuestionInputFocused: {
      type: Boolean,
      default: false
    },
    presetModelName: {
      type: String
    },
    isAnswering: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showModelSelector: {
      type: Boolean,
      default: true
    }
  },
  static: {
    AICypressAttribute
  },
  data() {
    return {
      modelName: this.presetModelName || OpenAIModel.GPT_5_6_TERRA.key,
      aiModelSelectConfig: new AiModelSelectConfig()
    };
  },
  computed: {
    question: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    canSubmit() {
      return !!this.question?.trim() && !this.disabled && !this.isAnswering;
    }
  },
  methods: {
    onQuestionSubmit() {
      this.$emit('on-submit', this.question.trim(), this.modelName);
    },
    focusQuestionInput() {
      this.$refs.questionInput?.focus?.();
    },
    onQuestionInputFocus() {
      this.$emit('on-question-input-focus');
    },
    onKeyUp(event) {
      if (event?.keyCode !== KeyCode.ENTER.value || event?.shiftKey) {
        this.$emit('on-keyup', this.question);
        return;
      }

      if (event.defaultPrevented || !this.canSubmit) {
        return;
      }

      event.preventDefault();

      this.onQuestionSubmit();
    }
  }
};
</script>

<style scoped>
.status-message {
  line-height: 1.1;
}

.textarea-container {
  padding-bottom: var(--ce-65px);
  transition: border-color 0.2s ease;
  &:hover {
    border-color: var(--ce-primary-light-20) !important;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: var(--ce-65px);
    left: 0;
    right: 0;
    height: var(--ce-5px);
    background: linear-gradient(to bottom, #fff0, #fff);
    pointer-events: none;
  }
}

:deep(.form-input) {
  margin-bottom: 0 !important;
}

:deep(.form-input textarea) {
  border: none;
  resize: none;
  background: var(--ce-white);
  color: var(--ce-black);
  border-radius: var(--ce-radius-large);
  max-height: var(--ce-235px);
  overflow-y: scroll !important;
  scrollbar-width: none;
}

:deep(.form-input textarea:active),
:deep(.form-input textarea:focus),
:deep(.form-input textarea:focus-visible) {
  border: none !important;
  box-shadow: none !important;
}
</style>
